import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui';
import TeamPlayerCard from '../components/team/TeamPlayerCard';
import {
    SeasonApiError,
    isSeasonFeatureDisabled,
    isSeasonAuthExpired,
    getSeasonDashboard,
    getSeasonRules,
    getSeasonRoster,
    getSeasonLineup,
    getSeasonPreseasonLineup,
    getSeasonSchedule,
    saveSeasonLineup,
    saveSeasonPreseasonLineup,
    submitSeasonLineup,
    startSeasonLeague,
    autodraftSeasonStragglers,
} from '../api/seasonApi';
import './SeasonTeamPage.css';

// =============================================================================
// SEASON CAMPAIGN TEAM PAGE — LIVE
// =============================================================================
// Backed by the real season API (AIDocs/Final/backend/api-reference-tdd.md
// §2.5a). Reads: dashboard, rules (slot definitions), roster, the week's
// persisted lineup, and the schedule (for this week's real matchup score).
// Writes: PUT lineup (full starter-map replacement) + POST lineup/submit.
//
// THREE CONTRACT FACTS THAT SHAPE THIS SCREEN:
//   1. `available_actions.can_edit_lineup` and `.can_start_league` are both
//      SERVER-DERIVED. `can_edit_lineup` is true only while the active week is
    //      LINEUPS_OPEN, or while a draft-complete owner configures their
    //      preseason lineup before Week 1 exists. `can_start_league` only while
    //      the league is FORMING, the caller IS the commissioner, and the joined
    //      team count is a legal league size. Neither is ever recomputed here —
    //      the server owns both decisions and a second implementation would drift.
//   2. The lineup PUT is a FULL REPLACEMENT: every required slot must be sent
//      in one call, and the backend derives the bench itself. So a swap builds
//      the complete starter map and sends all of it.
//   3. The roster endpoint returns identity only — no scores, no projections,
//      no matchup. Those render as "—", never as a fabricated 0.
//
// COMMISSIONER START IS ON THIS SCREEN BECAUSE NOTHING ELSE REACHES IT.
// A FORMING league has no other UI: My Teams lists the team, and clicking it
// lands here. Without the Start control below, `startSeasonLeague()` had no
// caller anywhere in the app, so a commissioner-created league stayed FORMING
// forever and its draft/regular season could never begin — a complete dead end.
//
// STARTING THE LEAGUE IS NOT WHAT OPENS THE DRAFT (2026-08-17, PR review).
// Drafting is available from FORMING — `serialize_league_dashboard()` sets
// `can_draft_initial_roster` for status in ('FORMING', 'DRAFTING'), and
// backend/tests/football/season/test_season_draft_to_regular_season.py:161-192
// drafts the creator while the league is still FORMING. Start closes membership
// and begins the season; it does not gate the initial roster draft.
//
// A team that still needs to draft is therefore routed to PACK_SELECTION by
// AppRouter (both from My Teams and now directly after create/join) and does not
// reach this screen at all — which is why this screen renders no draft control.
// If that routing is ever removed, `available_actions.can_draft_initial_roster`
// is the server-derived flag to render one from; do not infer it from status.
// =============================================================================

/**
 * Adapt one season roster row to the shared TeamPlayerCard's prop shape.
 *
 * `score`/`projected_points`/`matchup_display` are DELIBERATELY not set: the
 * season roster contract does not carry them. Inventing a 0 here would put a
 * number on screen that no backend ever produced.
 *
 * ⚠️ Omitting them is only SAFE because TeamPlayerCard was fixed to render an
 * explicit '—' for an absent value. It previously printed a hardcoded '0.0'
 * for a missing projection, so this "honest omission" was silently rendering a
 * fabricated projection for every season player — a number a manager could
 * reasonably have benched someone over. The lesson: omitting a value is only
 * honest if the RENDERER treats absence as unknown. Verify the renderer; do not
 * assume it.
 */
/**
 * The GEAR tab is LIVE for season teams (2026-08-28).
 *
 * It was previously inert because the gear UI is keyed on `CharacterSheet` end
 * to end and `serialize_roster()` omitted `character_sheet_id` — every season
 * player collapsed into one `undefined` bucket and equipped gear matched no
 * bucket at all, so a manager's equipped items simply vanished from the page.
 *
 * That field now ships (backend/football/season/serializers.py), and gear is
 * shared Team inventory in both modes — `Gear` hangs off `Team` and is equipped
 * to a `CharacterSheet`, neither of which is weekly-specific. The gear mutation
 * endpoints are season-aware (assert_generic_surface_authorized_for_season +
 * SeasonMutationGuard), so the freeze rules are enforced server-side per week
 * status rather than by hiding the tab.
 */
/**
 * Why tapping a season roster card does NOT open player detail (2026-08-16, PR review).
 *
 * `PlayerPage` is weekly-only end to end. Its load path calls
 * `getTeamData(teamId)` → `GET /v2/teams/<id>/stats/` → `TeamStatsView.get()`,
 * which calls `assert_weekly_league()` UNCONDITIONALLY
 * (backend/game/api/views/stats.py:102) and rejects season teams. `getTeamData()`
 * then swallows the non-2xx and returns null, so every season player click
 * landed on a generic "Failed to load team data" screen whose only button
 * emitted `onNavigate('HOME')` — a destination `handlePlayerPageNavigation` did
 * not handle. The user was stuck until they reloaded the browser.
 *
 * Swapping in the season roster would not rescue the screen: its GEAR tab needs
 * `character_sheet_id` (absent from the roster contract — the same field that
 * gates season gear), and SCORE / PROJ / the PTS breakdown all come from weekly
 * stats behind that rejected endpoint. The result would be a name over four
 * empty panels. Season player detail is UNBUILT, not one field short. See
 * AIDocs/Proposed/backend/season-player-detail-contract.md
 */
const SEASON_PLAYER_DETAIL_UNAVAILABLE_REASON =
    'Player details aren’t available for season campaigns yet — that screen is ' +
    'still daily-league only. Your roster, lineup and results are unaffected.';

/**
 * DISPLAY-ONLY suppression of starter slots — season mode has no FLEX yet.
 *
 * ⚠️ THIS HIDES A SLOT. IT DOES NOT REMOVE ONE. The distinction matters because
 * the slot is still REAL everywhere that counts:
 *
 *   • `slot_definitions_json` in the canonical season template still declares
 *     FLEX1 (backend/football/management/commands/load_season_league_templates.py),
 *     so `getSeasonRules()` still returns it.
 *   • The lineup PUT is a FULL REPLACEMENT and
 *     `SeasonLineupService.save_starters()` rejects any payload missing a
 *     required slot ("missing required slot_id(s)"). Dropping FLEX1 from the
 *     WRITE would 400 every single save.
 *   • Whoever occupies FLEX1 is a genuine starter and still scores server-side.
 *
 * So this set is consulted ONLY when deciding what to RENDER. Every write,
 * eligibility check and pending-pick calculation below continues to use the
 * complete `slotDefinitions` list. Deleting this constant restores the slot
 * with no other change — that is the point, since a FLEX may return later.
 *
 * NOTE ON THE OCCUPANT: a hidden slot's player is deliberately NOT shown on the
 * bench. The bench is (active roster − starters); listing an actual starter
 * there would tell a manager someone is benched while that player is still
 * scoring in their lineup. Hidden means hidden, not relabelled.
 */
const HIDDEN_SLOT_CODES = new Set(['FLEX1']);

/**
 * Adapt a season roster row to the shape TeamPlayerCard expects.
 *
 * Only fields the roster endpoint ACTUALLY returns are set. Score, projection,
 * matchup and team are all absent by contract — see the "no fabricated numbers"
 * rule; the card renders `—` for each rather than inventing a value.
 *
 * 🚨 PLAYER ARTWORK IS BACKEND-BLOCKED (2026-08-16, PR review).
 * `getPlayerImagePath()` builds `/art/player_images_gsis_cartoon/<id>.png` from
 * `gsis_id || source_player_id` — the nflverse GSIS id — and falls back to
 * `default.png` when neither is present. `serialize_roster()` currently emits
 * only `nfl_player_id`, which is the NFLPlayerMirror **database primary key**,
 * NOT `nflverse_id`. Those are different identifier spaces.
 *
 * So every season player renders the default avatar. The pass-through below is
 * live-on-arrival: the moment the serializer adds the field this starts working
 * with no frontend change.
 *
 * DO NOT map `nfl_player_id` into `gsis_id` to "fix" this. It would build
 * `/art/player_images_gsis_cartoon/4471.png`, 404, and — because the <img> has
 * no error fallback — show a broken image instead of the default avatar. A
 * wrong id is worse than a missing one. The correct fix is
 * `"gsis_id": state.nfl_player.nflverse_id` in
 * backend/football/season/serializers.py. See
 * AIDocs/Proposed/backend/season-roster-gsis-id.md
 */
function toCardPlayer(rosterRow) {
    return {
        id: rosterRow.league_player_id,
        name: rosterRow.nfl_player_name,
        position: rosterRow.position,
        // Undefined until the serializer supplies it → default avatar, which is
        // the honest rendering of "we have no artwork id for this player".
        gsis_id: rosterRow.gsis_id,
    };
}

function formatRecord(record) {
    if (!record) {
        return '—';
    }
    return `${record.wins}-${record.losses}-${record.ties}`;
}

function formatScore(value) {
    if (value === null || value === undefined) {
        return '—';
    }
    return value.toFixed(1);
}

/**
 * SeasonTeamPage — Season-Long Campaign roster screen.
 *
 * @param {number|string} teamId   - the season Team being viewed (required)
 * @param {number|string} leagueId - its League (required; every league-level
 *        read is keyed by it, and the dashboard is what tells us the active
 *        week and what the user may do)
 * @param {function} setPage - navigation callback
 */
function SeasonTeamPage({ teamId, leagueId, setPage }) {
    const [dashboard, setDashboard] = useState(null);
    const [rules, setRules] = useState(null);
    const [roster, setRoster] = useState([]);
    const [lineup, setLineup] = useState(null);
    const [myMatchup, setMyMatchup] = useState(null);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [featureDisabled, setFeatureDisabled] = useState(false);
    // Session gone (not a domain denial). seasonApi has already cleared cached
    // auth and begun redirecting to login; this only suppresses the useless
    // Retry and explains what happened during that hand-over.
    const [authExpired, setAuthExpired] = useState(false);

    // Swap interaction: the slot_code whose starter is awaiting a replacement.
    const [selectedSlotCode, setSelectedSlotCode] = useState(null);

    // LOCAL, UNSAVED slot assignments — slot_code -> league_player_id.
    //
    // Why this has to exist: the lineup PUT is a FULL REPLACEMENT and the
    // backend rejects an incomplete map outright (lineup_services.save_starters
    // → "missing required slot_id(s)"). So when a lineup arrives with more than
    // one empty slot, NO single assignment can be saved — the first N-1 picks
    // are unsavable by contract. Without somewhere to hold them the user could
    // never reach a complete map, which is the dead end this fixes.
    //
    // These are explicitly NOT server state and the UI labels them as unsaved.
    // The moment a complete map exists it is PUT and this clears.
    const [pendingBySlot, setPendingBySlot] = useState(() => new Map());
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [submitNotice, setSubmitNotice] = useState(null);

    // Commissioner "start league" is a separate in-flight flag from `saving`:
    // it is a different action on a different lifecycle stage (FORMING, when no
    // lineup exists at all), and sharing one flag would let a lineup save
    // disable the Start button or vice versa.
    const [startingLeague, setStartingLeague] = useState(false);

    // Separate again from `startingLeague`: these are two different commissioner
    // actions at two different lifecycle stages (FORMING vs DRAFTING) and are
    // never available simultaneously, but sharing a flag would couple them for
    // no reason.
    const [autodrafting, setAutodrafting] = useState(false);

    // Stale-response guard: week changes and manual refreshes can overlap, and
    // an older payload must never overwrite a newer one.
    const isMountedRef = useRef(true);
    const requestIdRef = useRef(0);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const loadAll = useCallback(async () => {
        if (!teamId || !leagueId) {
            throw new Error(
                `❌ SeasonTeamPage: both teamId and leagueId are required, got teamId=${JSON.stringify(
                    teamId
                )} leagueId=${JSON.stringify(leagueId)}`
            );
        }

        const requestId = ++requestIdRef.current;
        setLoading(true);
        setLoadError(null);
        setFeatureDisabled(false);
        setAuthExpired(false);

        try {
            const dashboardData = await getSeasonDashboard(leagueId);
            const activeWeek = dashboardData.current_week ? dashboardData.current_week.week : null;
            const shouldLoadPreseasonLineup = Boolean(
                activeWeek === null && dashboardData.available_actions.can_edit_lineup
            );

            const [rulesData, rosterData, lineupData, scheduleData] = await Promise.all([
                getSeasonRules(leagueId, activeWeek),
                getSeasonRoster(teamId),
                activeWeek === null
                    ? shouldLoadPreseasonLineup
                        ? getSeasonPreseasonLineup(teamId)
                        : Promise.resolve(null)
                    : getSeasonLineup(teamId, activeWeek),
                getSeasonSchedule(leagueId),
            ]);

            if (!isMountedRef.current || requestIdRef.current !== requestId) {
                console.debug(
                    `🕒 SeasonTeamPage: discarding superseded load (request #${requestId}, newest #${requestIdRef.current})`
                );
                return;
            }

            const numericTeamId = Number(teamId);
            const weekMatchup =
                activeWeek === null
                    ? null
                    : scheduleData.find(
                          (row) =>
                              row.week === activeWeek &&
                              (row.home_team_id === numericTeamId || row.away_team_id === numericTeamId)
                      ) || null;

            setDashboard(dashboardData);
            setRules(rulesData);
            setRoster(rosterData);
            setLineup(lineupData);
            setMyMatchup(weekMatchup);
        } catch (error) {
            if (!isMountedRef.current || requestIdRef.current !== requestId) {
                console.debug(
                    `🕒 SeasonTeamPage: discarding superseded load failure (request #${requestId}): ${error.message}`
                );
                return;
            }
            console.error('❌ SeasonTeamPage: failed to load season team data:', error);
            // A blocked feature flag is an expected answer, not a breakage —
            // it gets its own state so the screen can say so plainly.
            setFeatureDisabled(isSeasonFeatureDisabled(error));
            setAuthExpired(isSeasonAuthExpired(error));
            setLoadError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to load this season team'
            );
        } finally {
            if (isMountedRef.current && requestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    }, [teamId, leagueId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleNavigation = (destination) => {
        switch (destination) {
            case 'LEAGUES':
                setPage('HOME');
                break;
            case 'TEAM':
                // Already here
                break;
            case 'STANDINGS':
                // 'SEASON_STANDINGS', not 'LEAGUE'. The shared StandingsPage
                // calls the WEEKLY endpoint, which ranks Team.wins/losses and
                // the current week's WeeklyTeamStats (zero when absent) — while
                // season standings come from SEALED SeasonTeamWeekResult rows
                // ordered by the rules version's pinned tiebreak recipe. Nothing
                // on that endpoint rejects a season league, so it rendered a
                // plausible, silently WRONG table. See SeasonStandingsPage.
                setPage('SEASON_STANDINGS');
                break;
            case 'GEAR':
                // Shared gear page — it loads the SEASON roster branch off
                // `selectedLeagueMode`, which is already in sessionStorage by
                // the time this page renders.
                setPage('GEAR');
                break;
            default:
                throw new Error(`❌ SeasonTeamPage: unknown navigation destination: ${destination}`);
        }
    };

    const handlePlayerClick = () => {
        // DELIBERATELY DOES NOT NAVIGATE — see
        // SEASON_PLAYER_DETAIL_UNAVAILABLE_REASON. This used to write
        // `selectedPlayerId` and setPage('PLAYER'), which dropped the manager
        // into the weekly-only PlayerPage and a dead-ended error screen.
        //
        // Nothing is written to sessionStorage here either: leaving a stale
        // `selectedPlayerId`/`playerViewMode` behind would change what the
        // weekly player route does for whatever team is selected NEXT.
        setSaveError(SEASON_PLAYER_DETAIL_UNAVAILABLE_REASON);
    };

    // ---- Derived lineup model -------------------------------------------------

    // EVERY slot the rules version requires. This is the WRITE model — the
    // lineup PUT must carry all of these or the backend rejects it. Never
    // filter this list; filter what is rendered instead (see below).
    const slotDefinitions = useMemo(
        () => (rules ? [...rules.slot_definitions].sort((a, b) => a.display_order - b.display_order) : []),
        [rules]
    );

    // What the manager actually SEES. The only difference from the list above
    // is cosmetic suppression — see HIDDEN_SLOT_CODES.
    const visibleSlotDefinitions = useMemo(
        () => slotDefinitions.filter((slot) => !HIDDEN_SLOT_CODES.has(slot.slot_code)),
        [slotDefinitions]
    );

    const rosterById = useMemo(() => {
        const map = new Map();
        roster.forEach((row) => map.set(row.league_player_id, row));
        return map;
    }, [roster]);

    /** slot_code -> league_player_id, from the persisted lineup. */
    const startersBySlot = useMemo(() => {
        const map = new Map();
        if (!lineup) {
            return map;
        }
        lineup.players
            .filter((row) => row.is_starter && row.slot_code)
            .forEach((row) => map.set(row.slot_code, row.league_player_id));
        return map;
    }, [lineup]);

    /**
     * What the user currently sees: the persisted lineup with any unsaved local
     * assignments laid over it. Every consumer below (starter cards, bench
     * list, eligibility, the write itself) reads THIS, never `startersBySlot`
     * directly — otherwise a pending pick would show in one place and not
     * another.
     */
    const effectiveStartersBySlot = useMemo(() => {
        const merged = new Map(startersBySlot);
        pendingBySlot.forEach((leaguePlayerId, slotCode) => merged.set(slotCode, leaguePlayerId));
        return merged;
    }, [startersBySlot, pendingBySlot]);

    // Any freshly-loaded or freshly-saved server lineup supersedes local picks.
    useEffect(() => {
        setPendingBySlot(new Map());
    }, [lineup]);

    // Built from the FULL starter map, hidden slots included. A player in a
    // hidden slot is a real starter who really scores, so they must not fall
    // through to the bench list — see HIDDEN_SLOT_CODES. They are simply not
    // rendered anywhere while the slot is hidden.
    const starterPlayerIds = useMemo(
        () => new Set(effectiveStartersBySlot.values()),
        [effectiveStartersBySlot]
    );

    const benchRows = useMemo(
        () => roster.filter((row) => !starterPlayerIds.has(row.league_player_id)),
        [roster, starterPlayerIds]
    );

    // Slots still empty across persisted + pending — what stands between the
    // user and a saveable lineup.
    //
    // SPLIT BY VISIBILITY because the two are not the same problem. A visible
    // empty slot is the user's to fix by tapping it. A HIDDEN empty slot has no
    // affordance at all — the user cannot see it, so telling them to "fill
    // FLEX1" would name a slot that does not exist on their screen. Hidden
    // empties are resolved by auto-fill instead (see fillHiddenSlots).
    const unfilledVisibleSlotCodes = useMemo(
        () =>
            visibleSlotDefinitions
                .filter((slot) => effectiveStartersBySlot.get(slot.slot_code) === undefined)
                .map((slot) => slot.slot_code),
        [visibleSlotDefinitions, effectiveStartersBySlot]
    );

    // Hidden slots the SERVER still has empty. These block `is_complete` (and
    // therefore Submit) while being invisible, which is exactly the dead end
    // the repair banner below exists to break.
    const unfilledHiddenSlotCodes = useMemo(
        () =>
            slotDefinitions
                .filter(
                    (slot) =>
                        HIDDEN_SLOT_CODES.has(slot.slot_code) &&
                        effectiveStartersBySlot.get(slot.slot_code) === undefined
                )
                .map((slot) => slot.slot_code),
        [slotDefinitions, effectiveStartersBySlot]
    );
    const hasUnsavedPicks = pendingBySlot.size > 0;

    const canEditLineup = Boolean(
        dashboard && dashboard.available_actions && dashboard.available_actions.can_edit_lineup
    );
    // Server-derived, exactly like can_edit_lineup: true ONLY when the league
    // is FORMING, this user is the commissioner, and the joined team count is a
    // legal size. Never inferred from league.status here — the size and
    // commissioner-identity halves of that rule are not even visible to this
    // client, so any local guess would be wrong.
    const canStartLeague = Boolean(
        dashboard && dashboard.available_actions && dashboard.available_actions.can_start_league
    );
    // DRAFTING recovery. Unlike the two flags above there is NO server-derived
    // equivalent — `available_actions` has no `can_autodraft_stragglers`, and
    // the dashboard does not expose commissioner identity at all, so this is
    // the one place the client cannot ask the server "may I?".
    //
    // The compromise: OFFER the action to every member during DRAFTING, and let
    // the backend arbitrate. It already rejects a non-commissioner with 403
    // SEASON_NOT_COMMISSIONER, which this page surfaces verbatim. That is worth
    // it because the alternative is worse: without any control, one manager who
    // never drafts stalls the entire league permanently — the handoff waits for
    // every team and nothing times out.
    //
    // This is deliberately NOT a guess at permission (which would be the
    // fabricated-state bug this codebase keeps fighting). It is an honest
    // "attempt it and report what the server says", and the copy says so.
    const isDrafting = Boolean(dashboard && dashboard.league && dashboard.league.status === 'DRAFTING');
    const activeWeek = dashboard && dashboard.current_week ? dashboard.current_week.week : null;
    const isPreseasonLineup = Boolean(activeWeek === null && canEditLineup);

    const selectedSlot = selectedSlotCode
        ? slotDefinitions.find((slot) => slot.slot_code === selectedSlotCode) || null
        : null;

    const isEligibleForSelectedSlot = (rosterRow) =>
        Boolean(selectedSlot && selectedSlot.eligible_positions.includes(rosterRow.position));

    /**
     * Fill any EMPTY HIDDEN slot from the bench so a lineup the manager cannot
     * see does not silently block the one they can.
     *
     * WHY THIS IS NECESSARY, not a convenience: the PUT is a full replacement
     * and the backend requires every slot, so an empty FLEX1 makes the lineup
     * unsaveable and un-submittable — while being invisible on screen. Without
     * this, hiding the slot would strand any manager whose FLEX1 was empty,
     * with no control anywhere in the UI capable of fixing it.
     *
     * DETERMINISTIC AND NON-DESTRUCTIVE: it only ever consumes players who are
     * in NO slot at all (bench), and picks the first eligible one in roster
     * order. It never displaces a visible starter, and it never overwrites a
     * hidden slot that is already occupied — a manager's existing FLEX1 pick
     * from before this slot was hidden is preserved exactly as saved.
     *
     * Returns a NEW map; the caller decides whether to persist it.
     */
    const fillHiddenSlots = (startersBySlotMap) => {
        const filled = new Map(startersBySlotMap);
        const takenPlayerIds = new Set(filled.values());

        slotDefinitions.forEach((slot) => {
            if (!HIDDEN_SLOT_CODES.has(slot.slot_code)) {
                return;
            }
            if (filled.get(slot.slot_code) !== undefined) {
                return;
            }
            const candidate = roster.find(
                (row) =>
                    !takenPlayerIds.has(row.league_player_id) &&
                    slot.eligible_positions.includes(row.position)
            );
            if (candidate === undefined) {
                // Genuinely unfillable — the roster has nobody eligible left.
                // Deliberately NOT an exception: the lineup simply stays
                // incomplete and the banner below explains it, which is the
                // honest outcome. Fabricating an assignment would be worse.
                console.warn(
                    `⚠️ SeasonTeamPage: hidden slot ${slot.slot_code} has no eligible ` +
                        `bench player available (accepts ${slot.eligible_positions.join(', ')}).`
                );
                return;
            }
            filled.set(slot.slot_code, candidate.league_player_id);
            takenPlayerIds.add(candidate.league_player_id);
        });

        return filled;
    };

    // ---- Lineup writes --------------------------------------------------------
    //
    // ONE LINEUP MUTATION AT A TIME, ENFORCED IN TWO PLACES.
    //
    // The reads on this page are stale-guarded by generation id, but that
    // pattern does NOT work for these writes and must not be copied here: each
    // PUT is a FULL REPLACEMENT built from the CURRENT `startersBySlot`, so two
    // overlapping swaps both derive from the same pre-swap lineup and the second
    // silently discards the first. Discarding the older *response* would not
    // help — the damage is already committed server-side. The only correct
    // answer is to never have two in flight.
    //
    // `mutationInFlightRef` is a REF, not the `saving` state, because a second
    // click can land in the same tick before React re-renders; a state read
    // would still be `false`. The disabled buttons are the UX; this is the
    // correctness guarantee.
    const mutationInFlightRef = useRef(false);

    /**
     * Persist a starter map. FULL REPLACEMENT by contract — we always send
     * every slot, never just the one that changed.
     */
    const persistStarterMap = async (rawNextStartersBySlot) => {
        if (mutationInFlightRef.current) {
            // Expected under fast clicking, not an error: the controls are
            // already disabled, so this only fires on a same-tick double click.
            console.debug('🔒 SeasonTeamPage: lineup write already in flight — ignoring this swap');
            return;
        }
        // Top up invisible slots before building the payload. The backend
        // requires them and the user has no way to fill them by hand.
        const nextStartersBySlot = fillHiddenSlots(rawNextStartersBySlot);
        const starters = slotDefinitions
            .map((slot) => ({
                slot_code: slot.slot_code,
                league_player_id: nextStartersBySlot.get(slot.slot_code),
            }))
            .filter((entry) => entry.league_player_id !== undefined);

        if (starters.length !== slotDefinitions.length) {
            // NOT an error — this is the normal state while filling an empty
            // lineup. The PUT is a full replacement and the backend refuses a
            // partial map ("missing required slot_id(s)"), so an incomplete map
            // is held locally instead of being sent and rejected.
            //
            // Previously this returned an error and dropped the assignment on
            // the floor, which made a multi-slot-empty lineup impossible to
            // fill: every pick before the last was discarded.
            const missing = slotDefinitions
                .filter((slot) => nextStartersBySlot.get(slot.slot_code) === undefined)
                .map((slot) => slot.slot_code);
            const nextPending = new Map();
            slotDefinitions.forEach((slot) => {
                const assigned = nextStartersBySlot.get(slot.slot_code);
                if (assigned !== undefined && startersBySlot.get(slot.slot_code) !== assigned) {
                    nextPending.set(slot.slot_code, assigned);
                }
            });
            setPendingBySlot(nextPending);
            setSelectedSlotCode(null);
            setSaveError(null);

            // Only name slots the manager can actually see and act on. If the
            // remaining gap is a hidden slot with no eligible player left, say
            // that plainly instead of listing a slot code that appears nowhere
            // on screen — an instruction the user cannot follow is worse than
            // no instruction.
            const missingVisible = missing.filter((code) => !HIDDEN_SLOT_CODES.has(code));
            setSubmitNotice(
                missingVisible.length > 0
                    ? `Saved nothing yet — the whole lineup is written at once and ` +
                          `${missingVisible.join(', ')} still needs a player. Fill every slot and it saves automatically.`
                    : `Saved nothing yet — your roster doesn’t have enough eligible players ` +
                          `to complete this lineup. Add a player to your roster and it will save automatically.`
            );
            return;
        }

        mutationInFlightRef.current = true;
        setSaving(true);
        setSaveError(null);
        setSubmitNotice(null);
        try {
            const updated = isPreseasonLineup
                ? await saveSeasonPreseasonLineup(teamId, starters)
                : await saveSeasonLineup(teamId, activeWeek, starters);
            if (!isMountedRef.current) {
                return;
            }
            setLineup(updated);
            setSelectedSlotCode(null);
            if (isPreseasonLineup) {
                setSubmitNotice('Preseason lineup saved. It will seed Week 1 when the league begins.');
            }
        } catch (error) {
            console.error('❌ SeasonTeamPage: failed to save lineup:', error);
            if (!isMountedRef.current) {
                return;
            }
            setSaveError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to save the lineup'
            );
        } finally {
            // Cleared UNCONDITIONALLY — outside the isMounted check on purpose.
            // The ref outlives the mount check's intent: leaving it true after
            // an unmount-during-flight would permanently block writes if this
            // component instance were ever reused.
            mutationInFlightRef.current = false;
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    };

    /**
     * Position control on a STARTER card — arms (or disarms) that slot.
     */
    const handleStarterPositionClick = (slotCode) => {
        setSaveError(null);
        setSubmitNotice(null);
        setSelectedSlotCode((current) => (current === slotCode ? null : slotCode));
    };

    /**
     * Position control on a BENCH card — moves that player into the armed slot.
     * The displaced starter falls to the bench automatically: the backend
     * derives the bench as (active roster − starters), so simply reassigning
     * the slot is the entire operation.
     */
    const handleBenchPositionClick = (rosterRow) => {
        if (!selectedSlot) {
            return;
        }
        if (!isEligibleForSelectedSlot(rosterRow)) {
            setSaveError(
                `${rosterRow.nfl_player_name} (${rosterRow.position}) is not eligible for ` +
                    `${selectedSlot.slot_code} — that slot accepts ${selectedSlot.eligible_positions.join(', ')}.`
            );
            return;
        }
        // Built from the EFFECTIVE map so earlier unsaved picks are carried
        // forward rather than being lost on the next assignment.
        const next = new Map(effectiveStartersBySlot);
        next.set(selectedSlot.slot_code, rosterRow.league_player_id);
        persistStarterMap(next);
    };

    /**
     * Discard every unsaved local assignment and fall back to what the server
     * holds. The only way out of a half-built lineup that the user changed
     * their mind about.
     */
    const handleDiscardPendingPicks = () => {
        setPendingBySlot(new Map());
        setSelectedSlotCode(null);
        setSaveError(null);
        setSubmitNotice(null);
    };

    const handleSubmitLineup = async () => {
        // Shares the lineup-mutation lock with persistStarterMap(): a swap
        // landing mid-submit would clear the server's `submitted_at` stamp
        // AFTER this reported success, leaving the UI claiming "submitted" for
        // a lineup the backend no longer considers submitted.
        if (mutationInFlightRef.current) {
            console.debug('🔒 SeasonTeamPage: lineup write already in flight — ignoring this submit');
            return;
        }

        mutationInFlightRef.current = true;
        setSaving(true);
        setSaveError(null);
        setSubmitNotice(null);
        try {
            const updated = await submitSeasonLineup(teamId, activeWeek);
            if (!isMountedRef.current) {
                return;
            }
            setLineup(updated);
            setSubmitNotice('Lineup submitted. You can still edit it until the week locks.');
        } catch (error) {
            console.error('❌ SeasonTeamPage: failed to submit lineup:', error);
            if (!isMountedRef.current) {
                return;
            }
            setSaveError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to submit the lineup'
            );
        } finally {
            mutationInFlightRef.current = false;
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    };

    /**
     * Commissioner: start the league — FORMING → DRAFTING.
     *
     * This is the ONLY route out of FORMING in the entire app. Without it a
     * commissioner-created league is a dead end: the draft never opens, no week
     * is ever scheduled, and the lineup UI below can never become reachable.
     *
     * IRREVERSIBLE, so it is confirmed first: starting locks membership, so
     * anyone who has not joined yet never can. The backend re-checks
     * commissioner identity AND league size regardless of what this screen
     * showed — `can_start_league` gates the control, not the outcome.
     *
     * On success the whole page is reloaded rather than patched: starting
     * transitions the lifecycle and schedules week 1, so `current_week`, the
     * lineup and every `available_actions` flag all change at once. Re-reading
     * is the only way to reflect that honestly — hand-patching `dashboard`
     * would invent a client-side view of a lifecycle only the server knows.
     */
    const handleStartLeague = async () => {
        // No team count in the message: `serialize_league_dashboard()` returns
        // only {id, mode, status, name} for the league, so this client does not
        // know how many teams have joined. Quoting a number we do not have —
        // or fetching one just to decorate a dialog — would be worse than
        // leaving it out.
        const confirmed = window.confirm(
            'Start this campaign now?\n\n' +
            'This locks membership — nobody else can join afterwards — and opens ' +
            'the draft for every team currently in the league.\n\n' +
            'This cannot be undone.'
        );
        if (!confirmed) {
            return;
        }

        setStartingLeague(true);
        setSaveError(null);
        setSubmitNotice(null);
        try {
            await startSeasonLeague(leagueId);
            if (!isMountedRef.current) {
                return;
            }
            setSubmitNotice('Campaign started — the draft is now open.');
            await loadAll();
        } catch (error) {
            console.error('❌ SeasonTeamPage: failed to start the league:', error);
            if (!isMountedRef.current) {
                return;
            }
            setSaveError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to start the campaign'
            );
        } finally {
            if (isMountedRef.current) {
                setStartingLeague(false);
            }
        }
    };

    /**
     * Commissioner: autodraft everyone who hasn't finished, then complete the
     * handoff. THE RECOVERY PATH FOR A STALLED DRAFT.
     *
     * Without this, one manager who never drafts blocks the league forever: the
     * draft→regular-season handoff waits for every team and nothing times out.
     *
     * Also the documented retry for a failed launch — if a previous handoff died
     * after the last team committed, this re-runs it without re-drafting anyone.
     *
     * PERMISSION IS THE SERVER'S CALL. The dashboard exposes no commissioner
     * flag, so this is offered during DRAFTING and the backend arbitrates: a
     * non-commissioner gets 403 SEASON_NOT_COMMISSIONER, which is surfaced
     * verbatim rather than being pre-guessed here.
     *
     * PARTIAL FAILURE IS SURFACED IN FULL. On 409 SEASON_AUTODRAFT_FAILED the
     * error context carries a per-team `failures` array — that is exactly the
     * information a commissioner needs to know WHICH team is stuck, so it is
     * rendered rather than collapsed into a generic message. (Both fields are
     * already folded into the error message by SeasonApiError.)
     */
    const handleAutodraftStragglers = async () => {
        const confirmed = window.confirm(
            'Auto-draft every team that hasn’t finished?\n\n' +
            'Their rosters will be filled automatically and the regular season ' +
            'will begin. Use this when a manager has stopped responding and the ' +
            'league can’t start without them.\n\n' +
            'This cannot be undone.'
        );
        if (!confirmed) {
            return;
        }

        setAutodrafting(true);
        setSaveError(null);
        setSubmitNotice(null);
        try {
            const result = await autodraftSeasonStragglers(leagueId);
            if (!isMountedRef.current) {
                return;
            }
            const processed = typeof result?.teams_processed === 'number'
                ? result.teams_processed
                : null;
            setSubmitNotice(
                processed === null
                    ? 'Auto-draft complete — the regular season is starting.'
                    : `Auto-draft complete — ${processed} team(s) processed. The regular season is starting.`
            );
            // Same reasoning as handleStartLeague: this transitions the whole
            // lifecycle and schedules week 1, so re-read rather than patch.
            await loadAll();
        } catch (error) {
            console.error('❌ SeasonTeamPage: commissioner autodraft failed:', error);
            if (!isMountedRef.current) {
                return;
            }
            setSaveError(
                error instanceof SeasonApiError
                    ? `${error.errorCode}: ${error.message}`
                    : error.message || 'Failed to auto-draft the remaining teams'
            );
        } finally {
            if (isMountedRef.current) {
                setAutodrafting(false);
            }
        }
    };

    // ---- Render ---------------------------------------------------------------

    if (loading) {
        return (
            <div className="season-team-loading">
                <h2>Loading Season Team…</h2>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="season-team-error">
                <h2>
                    {featureDisabled
                        ? 'Season Campaign Unavailable'
                        : authExpired
                          ? 'Your Session Expired'
                          : 'Couldn’t Load This Team'}
                </h2>
                <p className="season-team-error-message">
                    {featureDisabled
                        ? 'Season-long campaigns are not enabled for your account yet.'
                        : authExpired
                          ? 'You’ve been signed out. Taking you back to the login screen…'
                          : loadError}
                </p>
                {/* NO RETRY for either terminal case. A blocked feature flag will
                    not change on retry, and an expired session cannot succeed —
                    seasonApi has already cleared cached auth and started the
                    redirect to login, so a Retry would just repeat the failure.
                    That loop is the bug this branch exists to end. */}
                {!featureDisabled && !authExpired && (
                    <Button onClick={loadAll} variant="secondary">
                        Retry
                    </Button>
                )}
                <Button onClick={() => setPage('HOME')} variant="secondary">
                    Back to Teams
                </Button>
            </div>
        );
    }

    const myTeam = dashboard.my_team;
    const numericTeamId = Number(teamId);
    // The UX half of the single-mutation rule (the ref in persistStarterMap is
    // the correctness half). Swap controls go inert while a write is in flight
    // so the user is never invited to start a second one — a click that would
    // be silently dropped is worse than a control that visibly cannot be used.
    const swapControlsEnabled = canEditLineup && !saving;
    const myScore = myMatchup
        ? myMatchup.home_team_id === numericTeamId
            ? myMatchup.home_score
            : myMatchup.away_score
        : null;

    const renderStarterCard = (slot) => {
        const leaguePlayerId = effectiveStartersBySlot.get(slot.slot_code);
        const rosterRow = leaguePlayerId === undefined ? undefined : rosterById.get(leaguePlayerId);

        if (!rosterRow) {
            // AN EMPTY SLOT MUST BE SELECTABLE — it is the ONLY way to fill it.
            //
            // This used to be an inert <div>. Because `selectedSlotCode` can
            // only be armed from a starter control, an empty slot had no
            // affordance, so bench clicks had no target and a lineup that
            // arrived empty or incomplete during LINEUPS_OPEN could never be
            // built or repaired. It is now a real button with the same arm /
            // disarm behaviour as an occupied slot.
            const isSelected = selectedSlotCode === slot.slot_code;
            return (
                <button
                    key={slot.slot_code}
                    type="button"
                    className={[
                        'season-empty-slot',
                        isSelected ? 'season-empty-slot--selected' : '',
                        swapControlsEnabled ? '' : 'season-empty-slot--static',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => handleStarterPositionClick(slot.slot_code)}
                    disabled={!swapControlsEnabled}
                    aria-pressed={isSelected}
                    aria-label={`${slot.slot_code} is empty. Accepts ${slot.eligible_positions.join(
                        ', '
                    )}. Select to choose a player.`}
                >
                    <span className="season-empty-slot-code">{slot.slot_code}</span>
                    <span className="season-empty-slot-text">
                        {swapControlsEnabled
                            ? isSelected
                                ? `Pick a bench player for ${slot.slot_code}`
                                : `Empty — tap to fill · eligible: ${slot.eligible_positions.join(', ')}`
                            : `Empty — eligible: ${slot.eligible_positions.join(', ')}`}
                    </span>
                </button>
            );
        }

        const isPending = pendingBySlot.has(slot.slot_code);
        return (
            <TeamPlayerCard
                key={slot.slot_code}
                player={{
                    ...toCardPlayer(rosterRow),
                    position: slot.slot_code,
                    // Unsaved picks are labelled as such — never presented as
                    // persisted state.
                    name: isPending
                        ? `${rosterRow.nfl_player_name} (unsaved)`
                        : rosterRow.nfl_player_name,
                }}
                onClick={handlePlayerClick}
                variant="season"
                swapEnabled={swapControlsEnabled}
                swapState={selectedSlotCode === slot.slot_code ? 'selected' : 'idle'}
                onPositionClick={() => handleStarterPositionClick(slot.slot_code)}
            />
        );
    };

    // NOTE ON THE CARD AFFORDANCE: the cards stay full-brightness and still
    // respond to a tap (which explains the limitation) rather than being dimmed
    // like the GEAR tab. The roster IS the primary content of this screen —
    // dimming every card to advertise a missing sub-screen would misrepresent
    // the roster itself as unavailable.
    const renderBenchCard = (rosterRow) => {
        let swapState = 'idle';
        if (selectedSlot) {
            swapState = isEligibleForSelectedSlot(rosterRow) ? 'eligible' : 'ineligible';
        }
        return (
            <TeamPlayerCard
                key={rosterRow.league_player_id}
                player={toCardPlayer(rosterRow)}
                onClick={handlePlayerClick}
                variant="season"
                swapEnabled={swapControlsEnabled}
                swapState={swapState}
                onPositionClick={() => handleBenchPositionClick(rosterRow)}
            />
        );
    };

    return (
        <div className="season-team-page">
            {/* Top Tab Bar */}
            <div className="season-tab-bar">
                <button className="season-tab" onClick={() => handleNavigation('LEAGUES')}>
                    LEAGUES
                </button>
                <button
                    className="season-tab season-tab--active"
                    onClick={() => handleNavigation('TEAM')}
                >
                    TEAM
                </button>
                <button className="season-tab" onClick={() => handleNavigation('STANDINGS')}>
                    STANDINGS
                </button>
                <button className="season-tab" onClick={() => handleNavigation('GEAR')}>
                    GEAR
                </button>
            </div>

            {/* Team Header Card */}
            <div className="season-team-header">
                <img src="/art/TeamIcon.png" alt="Team Icon" className="season-team-avatar" />
                <div className="season-team-identity">
                    <div className="season-team-name">{myTeam ? myTeam.name : dashboard.league.name}</div>
                    <div className="season-team-owner">
                        {dashboard.league.name} · {formatRecord(myTeam && myTeam.record)}
                    </div>
                </div>
                <div className="season-team-total">
                    <div className="season-team-total-label">
                        {activeWeek === null ? 'TEAM TOTAL' : `WEEK ${activeWeek}`}
                    </div>
                    <div className="season-team-total-score">{formatScore(myScore)}</div>
                </div>
            </div>

            {/* Week status strip — lifecycle is the whole story on this screen:
                a FINALIZED week's score is visible but NOT official. */}
            <div className="season-week-strip" role="status">
                <span className="season-week-status">
                    {activeWeek === null
                        ? `League ${dashboard.league.status.toLowerCase()} — no scheduled week yet`
                        : `Week ${activeWeek} · ${dashboard.current_week.status.replace('_', ' ')}`}
                </span>
                {myMatchup && myMatchup.is_provisional && myScore !== null && (
                    <span className="season-week-provisional">
                        Provisional — standings move only when the week is sealed
                    </span>
                )}
                {canEditLineup && (
                    <span className="season-week-editable">
                        {isPreseasonLineup
                            ? 'Preseason lineup open for edits'
                            : lineup && lineup.submitted_at
                              ? 'Lineup submitted'
                              : 'Lineup open for edits'}
                    </span>
                )}
            </div>

            {/* COMMISSIONER START — the only exit from FORMING in the whole app.
                Rendered as a prominent banner rather than a quiet footer button
                because the league is otherwise inert: there is no lineup, no
                week and no draft until this is pressed, so a commissioner who
                misses it has a permanently stuck campaign. */}
            {canStartLeague && (
                <div className="season-commissioner-panel" role="region" aria-label="Commissioner actions">
                    <div className="season-commissioner-copy">
                        <span className="season-commissioner-title">
                            You’re the commissioner
                        </span>
                        <span className="season-commissioner-detail">
                            This campaign has enough teams to begin. Starting locks
                            membership and opens the draft — it can’t be undone.
                        </span>
                    </div>
                    <Button
                        onClick={handleStartLeague}
                        variant="primary"
                        disabled={startingLeague}
                    >
                        {startingLeague ? 'Starting…' : 'Start Campaign'}
                    </Button>
                </div>
            )}

            {/* COMMISSIONER RECOVERY — the only exit from a STALLED DRAFT.
                The handoff to the regular season waits for EVERY team, and
                nothing times out, so a single manager who never drafts blocks
                the league permanently. This is also the retry when a previous
                launch failed after the last team completed.

                Shown to every member during DRAFTING because the dashboard
                exposes no commissioner flag — the copy says the action is
                commissioner-only, and the backend enforces it with a 403 that
                this page surfaces verbatim. */}
            {isDrafting && (
                <div className="season-commissioner-panel" role="region" aria-label="Commissioner recovery">
                    <div className="season-commissioner-copy">
                        <span className="season-commissioner-title">
                            Draft in progress
                        </span>
                        <span className="season-commissioner-detail">
                            Waiting on managers who haven’t finished drafting. The
                            commissioner can auto-draft them to start the season —
                            everyone else will get a “not the commissioner” message.
                        </span>
                    </div>
                    <Button
                        onClick={handleAutodraftStragglers}
                        variant="primary"
                        disabled={autodrafting}
                    >
                        {autodrafting ? 'Auto-drafting…' : 'Auto-draft Remaining Teams'}
                    </Button>
                </div>
            )}

            {saveError && (
                <div className="season-lineup-error" role="alert">
                    {saveError}
                </div>
            )}
            {submitNotice && (
                <div className="season-lineup-notice" role="status">
                    {submitNotice}
                </div>
            )}
            {lineup && lineup.warnings.length > 0 && (
                <div className="season-lineup-notice" role="status">
                    {lineup.warnings.join(' · ')}
                </div>
            )}

            {/* HIDDEN-SLOT REPAIR — the escape hatch for an invisible blocker.
                A saved lineup with an empty hidden slot is incomplete server-side,
                so Submit stays disabled, but the empty slot is not on screen and
                the manager has nothing to tap. Auto-fill normally resolves this
                on the next swap; this button covers the case where the manager
                makes no swap at all and would otherwise be stuck with a lineup
                that "looks finished" but cannot be submitted. */}
            {canEditLineup && !saving && unfilledHiddenSlotCodes.length > 0 && (
                <div className="season-lineup-notice" role="status">
                    Your lineup isn’t complete yet — one of your roster spots still
                    needs a player. We can fill it from your bench automatically.
                    <Button
                        onClick={() => persistStarterMap(effectiveStartersBySlot)}
                        variant="secondary"
                        disabled={saving}
                    >
                        Complete My Lineup
                    </Button>
                </div>
            )}

            {/* Starting Lineup */}
            <div className="season-roster-section">
                <div className="season-section-title-row">
                    <span className="season-section-title season-section-title--muted">
                        {isPreseasonLineup ? 'PRESEASON LINEUP' : 'STARTING LINEUP'}
                    </span>
                    {canEditLineup && (
                        <span className="season-swap-hint">
                            {saving
                                ? 'Saving…'
                                : selectedSlot
                                  ? `Pick a bench player for ${selectedSlot.slot_code}`
                                  : unfilledVisibleSlotCodes.length > 0
                                    ? `Tap an empty slot to fill it — ${unfilledVisibleSlotCodes.join(', ')} still open`
                                    : 'Tap a position to swap'}
                        </span>
                    )}
                    {canEditLineup && hasUnsavedPicks && (
                        <button
                            type="button"
                            className="season-discard-pending"
                            onClick={handleDiscardPendingPicks}
                            disabled={saving}
                        >
                            Discard unsaved picks
                        </button>
                    )}
                </div>
                <div className="season-card-stack">
                    {visibleSlotDefinitions.length === 0 ? (
                        <div className="season-no-players">
                            No starting slots are defined for the active rules version.
                        </div>
                    ) : (
                        // Renders the VISIBLE list only. Writes still use the
                        // complete `slotDefinitions` — see HIDDEN_SLOT_CODES.
                        visibleSlotDefinitions.map(renderStarterCard)
                    )}
                </div>
            </div>

            {/* Bench */}
            <div className="season-roster-section">
                <div className="season-section-title-row">
                    <span className="season-section-title">Bench</span>
                </div>
                <div className="season-card-stack">
                    {benchRows.length === 0 ? (
                        <div className="season-no-players">No bench players on this roster.</div>
                    ) : (
                        benchRows.map(renderBenchCard)
                    )}
                </div>
            </div>

            <div className="season-team-footer">
                {canEditLineup && !isPreseasonLineup && (
                    <Button
                        onClick={handleSubmitLineup}
                        variant="primary"
                        // `hasUnsavedPicks` is part of the guard because a
                        // pending pick is not persisted: submitting would
                        // submit the SERVER's lineup, not the one on screen.
                        disabled={saving || hasUnsavedPicks || !lineup || !lineup.is_complete}
                    >
                        {saving ? 'Working…' : 'Submit Lineup'}
                    </Button>
                )}
                <Button onClick={() => setPage('HOME')} variant="secondary">
                    Back to Teams
                </Button>
            </div>
        </div>
    );
}

export default SeasonTeamPage;
